// src/App.jsx
import React, { lazy, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { ErrorBoundary } from '@sentry/react';
import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import Shortcode from './Shortcode';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorComponent from './ErrorComponent';
import Fallback from './Fallback.jsx';

import { useConfig } from './contexts/ConfigContext';
import { useAppState } from './contexts/AppStateContext';
import { useCart } from './contexts/CartContext';
import { useRetailerData } from './hooks/dutchie-queries.js';
import { useInitialURLParams } from './hooks/useInitialURLParams';

const CartSidebarIcon = lazy(() => import('./components/Cart/CartSidebarIcon'));
const CartSidebarWrapper = lazy(() => import('./components/Cart/CartSidebarWrapper'));
const LocationModal = lazy(() => import('./components/LocationSelect/LocationModal'));

const App = ({ fallback }) => {
    const initialParams = useInitialURLParams();
    const params = useParams();

    // Get all needed values from contexts
    const {
        retailers,
        nonHeadlessRetailers,
        shortcodeData,
    } = useConfig();

    const {
        isInitialized,
        isAppReady,
        retailerId,
        setRetailerId,
        currentRetailer,
        nonHeadlessRetailer,
        setNonHeadlessRetailer,
        nonHeadlessRetailerData,
        setNonHeadlessRetailerData,
        fallbackEnabled,
        gatesPassed,
        needsLocationGate,
        needsAgeGate,
        passAgeGate,
        shouldRenderCart,
    } = useAppState();

    const { isFetched: isFetchedCartData } = useCart();
    const { isFetched: isFetchedRetailerData } = useRetailerData(retailerId);

    // Check if this is a single product page for SEO purposes
    const isSingleProductPage = params?.categoryType === 'product';

    // For SEO: Determine if we should render content in the background
    const shouldRenderContentForSEO = isSingleProductPage && 
        isInitialized && 
        retailerId && 
        !fallbackEnabled;

    // Simple state checks - no complex logic
    if (!isInitialized) {
        // Determine appropriate loading type based on shortcode
        const getLoadingType = () => {
            // Get the first shortcode type from shortcodeData
            const firstDataset = Object.values(shortcodeData)[0];
            const shortcode = firstDataset?.shortcode;

            switch (shortcode) {
                case 'shopnav':
                case 'shopnavfull':
                    return 'shopNav';
                case 'headlessmenu':
                    return 'menuList';
                default:
                    return 'list';
            }
        };

        return <LoadingSpinner loadingType={getLoadingType()} />;
    }

    // Fallback mode
    if (fallbackEnabled && retailerId) {
        return (
            <>
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const fallbackElement = (
                        <Fallback
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            script={fallback[retailerId].script}
                        />
                    );

                    return i === 0 ? fallbackElement : createPortal(fallbackElement, root);
                })}
            </>
        );
    }

    // Gates not passed - but render content for SEO if it's a single product page
    if (!gatesPassed && !nonHeadlessRetailer) {
        return (
            <>
                {/* Always show the location modal for user interaction */}
                {createPortal(
                    <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <Suspense fallback={null}>
                            <Helmet>
                                <link rel="canonical" href={window.location.href} />
                            </Helmet>
                            <LocationModal
                                retailers={retailers}
                                retailerId={retailerId}
                                onRetailerSelect={setRetailerId}
                                onAgeGatePass={passAgeGate}
                                showLocationGate={needsLocationGate}
                                showAgeGate={needsAgeGate && !needsLocationGate}
                                nonHeadlessRetailer={nonHeadlessRetailer}
                                setNonHeadlessRetailer={setNonHeadlessRetailer}
                                nonHeadlessRetailerData={nonHeadlessRetailerData}
                                setNonHeadlessRetailerData={setNonHeadlessRetailerData}
                            />
                        </Suspense>
                    </ErrorBoundary>,
                    document.body
                )}

                {/* Render content for SEO if it's a single product page */}
                {shouldRenderContentForSEO && (
                    <div style={{ 
                        position: 'absolute', 
                        left: '-9999px', 
                        top: '-9999px',
                        visibility: 'hidden',
                        // Ensure it's still accessible to screen readers and crawlers
                        clip: 'rect(0, 0, 0, 0)',
                        height: '1px',
                        overflow: 'hidden',
                        width: '1px'
                    }}>
                        <Helmet>
                            <link rel="canonical" href={window.location.href} />
                        </Helmet>
                        <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                            {/* Render shortcodes for SEO */}
                            {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                                const shortcodeElement = (
                                    <Shortcode
                                        key={`seo-${rootId}`}
                                        rootId={rootId}
                                        shortcodeData={dataset}
                                        forcedOrderType={initialParams}
                                    />
                                );
                                return shortcodeElement;
                            })}
                        </ErrorBoundary>
                    </div>
                )}
            </>
        );
    }

    // Waiting for data
    if (!isAppReady || (!isFetchedRetailerData && retailerId) || (!isFetchedCartData && retailerId)) {
        // Determine appropriate loading type based on shortcode
        const getLoadingType = () => {
            const firstDataset = Object.values(shortcodeData)[0];
            const shortcode = firstDataset?.shortcode;

            switch (shortcode) {
                case 'shopnav':
                case 'shopnavfull':
                    return 'shopNav';
                case 'headlessmenu':
                    return 'menuList';
                default:
                    return 'list';
            }
        };

        return <LoadingSpinner loadingType={getLoadingType()} />;
    }

    // Main app render - just display components
    return (
        <>
            <Helmet>
                <link rel="canonical" href={window.location.href} />
            </Helmet>
            <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                {/* Render shortcodes */}
                {Object.entries(shortcodeData).map(([rootId, dataset], i) => {
                    const root = document.getElementById(rootId);
                    const shortcodeElement = (
                        <Shortcode
                            key={rootId}
                            rootId={rootId}
                            shortcodeData={dataset}
                            forcedOrderType={initialParams}
                        />
                    );

                    return i === 0 ? shortcodeElement : createPortal(shortcodeElement, root);
                })}

                {/* Render cart sidebar if needed */}
                {shouldRenderCart && createPortal(
                    <ErrorBoundary fallback={(props) => <ErrorComponent {...props} />}>
                        <Suspense fallback={null}>
                            <CartSidebarIcon fixedCart={true} />
                            <CartSidebarWrapper fixedCart={true} forcedOrderType={initialParams} />
                        </Suspense>
                    </ErrorBoundary>,
                    document.body
                )}
            </ErrorBoundary>
        </>
    );
};

export default App;
